<script setup lang="ts">
import { RouterLink, RouterView, type Router, useRoute } from "vue-router";
import { onMounted, watch, watchEffect, ref, computed } from "vue";
import { useCookies } from "vue3-cookies";
import apiClient from "./config/apiClient";
import { useAuthStore } from "@/stores/auth";
import { useOrgStore, type OrganizationWithRole } from "@/stores/org";
import { useRole } from "@/composables/useRole";
import router from "./router";
import { isEmpty, capitalize } from "lodash";
import jsmLogo from "./assets/jsm_logo.png";
import ToastHost from "@/components/ToastHost.vue";

const { cookies } = useCookies();
const API_URL = import.meta.env.VITE_API_URL;
const authStore: any = useAuthStore();
const orgStore = useOrgStore();
const { hasRole } = useRole();

// Label shown under the user's name in the profile dropdown. Falls back to
// "Superadmin" when there's no org role to show (e.g. a platform operator
// with no org of their own) — mirrors the isSuperadmin carve-out hasRole()
// itself applies.
const roleLabel = computed<string>(() => {
  if (orgStore.currentRole) return capitalize(orgStore.currentRole);
  if (authStore.isSuperadmin) return "Superadmin";
  return "";
});

const route: any = useRoute();

const loading = ref(false);

// Same badge-per-role mapping used by OrgSelect.vue and OrgSettings.vue —
// kept local (not shared) to match how those two already duplicate it.
const roleBadgeClass = (role: string): string => {
  switch (role) {
    case "owner":
      return "badge badge-primary";
    case "admin":
      return "badge badge-secondary";
    case "finance":
      return "badge badge-accent";
    case "staff":
      return "badge badge-ghost";
    default:
      return "badge";
  }
};

// Reuses OrgSelect.vue's exact switch logic: set the active org (which also
// persists it and drives the X-Organization-ID header via apiClient). Unlike
// OrgSelect.vue — which is only ever entered from a *different* route, so
// `router.push("/")` always remounts the dashboard — this switcher can be
// used from any page, including Home itself. A SPA navigation to the same
// route is a no-op, leaving every already-fetched, org-scoped page (Home's
// event list, an open event, etc.) showing stale data under the new org
// label. A hard navigation forces every page to refetch under the new org.
const switchOrg = (org: OrganizationWithRole): void => {
  orgStore.selectOrg(org);
  window.location.href = "/";
};

const logout = async () => {
  authStore.user = {};
  orgStore.clearOrg();
  cookies.remove("Convena-AccessToken");
  cookies.remove("Convena-FirebaseAccessToken");
  router.push("/login");
  console.log("logout");
};

const checkRouteGuard = () => {
  // Was a hardcoded ["/login", "/loginAdmin"] list that never got updated as
  // new public routes were added (Register, VerifyEmail's own bypass logic
  // aside, and now AcceptInvite) — any of them hit via a hard page load (the
  // only time this onMounted-driven guard runs; SPA navigations don't remount
  // App.vue) got force-redirected to /login before ever rendering. Reading
  // route.meta.public keeps this in sync with the same flag router/index.ts's
  // beforeEach already uses, instead of a second, separately-maintained list.
  const authRequired = !route.meta?.public;
  const anonOnly = route.name === "login" || route.name === "Register";

  if (
    authRequired &&
    (isEmpty(authStore.user) || cookies.get("Convena-AccessToken") == null)
  ) {
    authStore.user = {};
    cookies.remove("Convena-AccessToken");
    cookies.remove("Convena-FirebaseAccessToken");
    router.push("/login");
  }
  if (
    anonOnly &&
    !isEmpty(authStore.user) &&
    cookies.get("Convena-AccessToken") !== null
  ) {
    router.push("/");
  }
};

onMounted(() => {
  loading.value = true;

  if (cookies.get("Convena-AccessToken")) {
    loading.value = true;
    apiClient
      .get(API_URL + "/api/userAdmin")
      .then((response) => {
        authStore.user = response.data.user;
        authStore.roles = response.data.roles;
        authStore.permissions = response.data.permissions;
        loading.value = false;
      })
      .then(async () => {
        // Multi-tenancy: restore the previously-selected org from
        // localStorage and validate it against the user's accessible orgs.
        // Failures here (network, 401, etc.) are intentionally swallowed —
        // the user will land without a currentOrg and can pick one.
        try {
          await orgStore.restoreOrg();
        } catch (e) {
          console.log("restoreOrg failed", e);
        }
        checkRouteGuard();
      })
      .catch((error) => {
        console.log(error);
        loading.value = false;
      });
  } else {
    loading.value = false;
    checkRouteGuard();
  }
  // User is signed out
  // ...
});
</script>

<template>
  <!-- Phase 5.6: the VerifyEmail and Onboarding routes are "stuck" screens.
       Hide the topbar so the user can't navigate around them. -->
  <div
    v-if="!['VerifyEmail', 'Onboarding'].includes(String(route.name ?? ''))"
    class="navbar bg-base-100 border-b border-base-300 shadow-sm"
  >
    <div class="flex-none">
      <RouterLink to="/" class="btn btn-ghost px-2">
        <img src="/convena-logo-primary.svg" alt="Convena Admin" class="h-8" />
      </RouterLink>
    </div>
    <div class="flex-1">
      <ul class="menu menu-horizontal p-0">
        <li>
          <RouterLink to="/" class="mx-2">Home</RouterLink>
        </li>
        <!-- /api/user and /api/group are both scope:SUPERADMIN on the backend
             (routes/api.php), so these links only make sense for superadmins. -->
        <li v-if="authStore.isSuperadmin">
          <RouterLink to="/member" class="mx-2">Member</RouterLink>
        </li>
        <li v-if="authStore.isSuperadmin">
          <RouterLink to="/cellGroup" class="mx-2">Kelompok Sel</RouterLink>
        </li>
        <!-- Org switcher: only worth showing when there's actually a choice.
             Reuses orgStore.selectOrg — the same call OrgSelect.vue makes —
             so switching here is equivalent to going through /select-org. -->
        <li v-if="orgStore.userOrgs.length > 1" tabindex="0">
          <div class="dropdown dropdown-bottom">
            <label
              tabindex="0"
              class="flex"
              data-testid="org-switcher-toggle"
            >
              {{ orgStore.currentOrg?.name }}
              <svg
                class="fill-current"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
              >
                <path
                  d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                />
              </svg>
            </label>
            <ul
              tabindex="0"
              class="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-64"
            >
              <li v-for="org in orgStore.userOrgs" :key="org.id">
                <a
                  data-testid="org-switcher-option"
                  @click="switchOrg(org)"
                >
                  <span class="flex-1 truncate">{{ org.name }}</span>
                  <span :class="roleBadgeClass(org.pivot.role)">
                    {{ org.pivot.role }}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </li>
        <li v-if="hasRole(['owner', 'admin', 'staff', 'finance'])">
          <RouterLink to="/admin/event" class="mx-2">Event</RouterLink>
        </li>
        <li v-if="hasRole(['owner', 'admin', 'staff', 'finance'])">
          <RouterLink to="/admin/location" class="mx-2">Lokasi</RouterLink>
        </li>
        <li v-if="hasRole(['owner', 'admin', 'staff', 'finance'])">
          <RouterLink to="/admin/question" class="mx-2">Pertanyaan</RouterLink>
        </li>
      </ul>
    </div>
    <div class="flex-none gap-2">
      <div
        class="dropdown dropdown-end"
        v-if="isEmpty(authStore.user) || !cookies.get('Convena-AccessToken')"
      >
        <RouterLink to="/login" class="mx-2"> Login </RouterLink>
      </div>

      <div v-else class="dropdown dropdown-end">
        <label tabindex="0" class="btn btn-ghost btn-circle avatar" data-testid="user-menu-toggle">
          <div class="w-10 rounded-full">
            <img
              :src="authStore.user?.profile_photo_path || '/convena-icon-square.svg'"
            />
          </div>
        </label>
        <ul
          tabindex="0"
          class="mt-3 p-2 shadow menu menu-compact dropdown-content bg-base-100 rounded-box w-52"
        >
          <li class="pointer-events-none px-4 pt-1 pb-2">
            <div class="flex flex-col items-start gap-0 p-0 hover:bg-transparent">
              <span class="font-medium" data-testid="user-name-label">
                {{ authStore.user?.name }}
              </span>
              <span
                v-if="roleLabel"
                class="text-xs text-base-content/50"
                data-testid="user-role-label"
              >
                {{ roleLabel }}
              </span>
            </div>
          </li>
          <li>
            <RouterLink :to="{ name: 'Profile' }" data-testid="profile-link">
              Profile
            </RouterLink>
          </li>
          <li v-if="orgStore.currentOrg && hasRole(['owner', 'admin', 'staff', 'finance'])">
            <RouterLink :to="{ name: 'OrgSettings' }">
              Organization
              <span class="text-xs text-base-content/50 ml-1" data-testid="current-org-name">
                {{ orgStore.currentOrg.name }}
              </span>
            </RouterLink>
          </li>
          <li v-if="hasRole(['owner', 'admin'])">
            <a data-testid="settings-link">Settings</a>
          </li>
          <!-- Phase 7.3: Billing — owner-only. Staff/finance don't manage
               billing. Sits with org-scoped items above the operator divider. -->
          <li v-if="hasRole('owner')">
            <RouterLink :to="{ name: 'Billing' }" data-testid="billing-link">Billing</RouterLink>
          </li>
          <!-- Phase 6.3: Platform-admin link — visible only to superadmins.
               Visually separated from the org-scoped items above to make it
               clear this is operator-level, not org-level. -->
          <li
            v-if="authStore.isSuperadmin"
            class="border-t border-base-200 mt-1 pt-1"
          >
            <RouterLink
              :to="{ name: 'SuperadminDashboard' }"
              class="text-primary font-medium"
            >
              Platform admin
            </RouterLink>
          </li>
          <!-- Tier-payment verification link — visible to the platform_admin
               role (or superadmins, via isPlatformAdmin's own bypass). Kept
               as a separate entry from "Platform admin" above since the two
               are gated by different flags (is_superadmin vs the
               platform_admin role) and a platform_admin-only account
               shouldn't see the superadmin org-management panel. -->
          <li
            v-if="authStore.isPlatformAdmin"
            :class="{ 'border-t border-base-200 mt-1 pt-1': !authStore.isSuperadmin }"
          >
            <RouterLink
              :to="{ name: 'PlatformBilling' }"
              class="text-primary font-medium"
              data-testid="platform-billing-link"
            >
              Tier Payments
            </RouterLink>
          </li>
          <!-- All-events browse: additive visibility view alongside Tier
               Payments, not a replacement for it — same gate/audience, so
               grouped right next to it rather than getting its own divider. -->
          <li v-if="authStore.isPlatformAdmin">
            <RouterLink
              :to="{ name: 'PlatformEvents' }"
              class="text-primary font-medium"
              data-testid="platform-events-link"
            >
              All Events
            </RouterLink>
          </li>
          <li><a @click="logout" data-testid="logout-btn">Logout</a></li>
        </ul>
      </div>
    </div>
  </div>
  <div v-if="loading">
    <div class="w-full h-screen flex items-center justify-center">
      <button class="btn btn-lg loading no-animation">Loading</button>
    </div>
  </div>
  <div v-else>
    <div class="max-w-7xl mx-auto">
      <RouterView />
    </div>
  </div>

  <!-- Global error notifications, fed by apiClient's response interceptor.
       Outside the loading branch so a failed bootstrap request still shows. -->
  <ToastHost />
</template>

<style scoped></style>
